import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Changelog from '../components/Changelog.jsx';
import ModuleCard from '../components/ModuleCard.jsx';
import { getModulesForHome } from '../config/modules.js';
import ExternalDataAppsBar from '../components/home/ExternalDataAppsBar.jsx';
import HomePanel from '../components/home/HomePanel.jsx';
import ModulesSearchInput from '../components/home/ModulesSearchInput.jsx';
import { THIRD_PARTY_DATA_APPS } from '../config/externalDataApps.js';

function Home({ currentUser }) {
  const [loading, setLoading] = useState(true);
  const [moduleSearch, setModuleSearch] = useState('');
  const moduleSearchRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const wait1s = async () => {
      await new Promise(resolve => setTimeout(resolve, 100));
      setLoading(false);
    };
    wait1s();
  }, []);

  useEffect(() => {
    if (!loading) {
      moduleSearchRef.current?.focus();
    }
  }, [loading]);

  // Hidden modules (e.g. PULSE_EDITOR) are grants, not cards: never shown, never matched by the search.
  const modules = getModulesForHome(currentUser).filter((module) => !module.hide);
  const filteredModules = useMemo(() => {
    const q = moduleSearch.trim();
    if (!q) return modules;

    const normalize = (s) =>
      String(s || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');

    const nq = normalize(q);

    return modules.filter((m) => {
      const hay = normalize(`${m.name} ${m.description || ''}`);
      return hay.includes(nq);
    });
  }, [moduleSearch, modules]);

  const onSearchKeyDown = (e) => {
    if (e.key === 'Escape') {
      setModuleSearch('');
      return;
    }

    if (e.key !== 'Enter') return;

    if (filteredModules.length !== 1) return;
    const only = filteredModules[0];
    if (!only?.hasAccess) return;
    navigate(only.path);
  };

  if (loading) {
    return (
      <div className="container mx-auto p-4">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-4"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-8"></div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <div className="h-44 bg-gray-200 dark:bg-gray-700 rounded-xl"></div>
              <div className="h-80 bg-gray-200 dark:bg-gray-700 rounded-xl"></div>
            </div>
            <div className="lg:col-span-1 h-96 bg-gray-200 dark:bg-gray-700 rounded-xl"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-b from-gray-50 to-white dark:from-gray-950 dark:to-gray-900">
      <div className="container mx-auto px-4 py-6">
        <div className="mb-6">
          <ExternalDataAppsBar apps={THIRD_PARTY_DATA_APPS} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <HomePanel
              title="Modules Insights"
              icon={(
                <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                </svg>
              )}
              rightSlot={(
                <ModulesSearchInput
                  value={moduleSearch}
                  onChange={(e) => setModuleSearch(e.target.value)}
                  onKeyDown={onSearchKeyDown}
                  inputRef={moduleSearchRef}
                />
              )}
              className="lg:h-[calc(100vh-12rem)]"
              bodyClassName="p-4 overflow-y-auto"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {modules.length > 0 ? (
                  filteredModules.length > 0 ? (
                    filteredModules.map((module) => {
                      return <ModuleCard
                        key={module.id}
                        module={module}
                        hasAccess={module.hasAccess}
                        compact
                      />
                    })
                  ) : (
                    <div className="md:col-span-2 xl:col-span-3 text-center py-10">
                      <div className="text-gray-500 dark:text-gray-400">
                        <svg className="mx-auto h-10 w-10 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9h8M8 13h6m2 8H8a2 2 0 01-2-2V7a2 2 0 012-2h6l4 4v10a2 2 0 01-2 2z" />
                        </svg>
                        <h3 className="text-base font-medium mb-1">Aucun résultat</h3>
                        <p className="text-sm">Essayez une autre recherche.</p>
                      </div>
                    </div>
                  )
                ) : (
                  <div className="md:col-span-2 xl:col-span-3 text-center py-10">
                    <div className="text-gray-500 dark:text-gray-400">
                      <svg className="mx-auto h-10 w-10 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <h3 className="text-base font-medium mb-1">Aucun module disponible</h3>
                      <p className="text-sm">Contactez votre administrateur pour obtenir les permissions nécessaires.</p>
                    </div>
                  </div>
                )}
              </div>
            </HomePanel>
          </div>

          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-6">
              <HomePanel
                title="Nouveautés"
                icon={(
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )}
                bodyClassName="p-0"
                className="lg:h-[calc(100vh-12rem)]"
              >
                <div className="p-4 h-full overflow-y-auto">
                  <Changelog currentUser={currentUser} showHeader={false} />
                </div>
              </HomePanel>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Home;